import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { BookOpen, X } from "lucide-react";

// Module-level cache so the glossary is fetched once per page load
let _glossaryCache = null;
let _glossaryPromise = null;
async function loadGlossary() {
  if (_glossaryCache) return _glossaryCache;
  if (!_glossaryPromise) {
    _glossaryPromise = api.get("/glossary").then((r) => {
      _glossaryCache = r.data || {};
      return _glossaryCache;
    }).catch(() => {
      _glossaryCache = {};
      return _glossaryCache;
    });
  }
  return _glossaryPromise;
}

/**
 * Renders the track vocabulary as clickable pills. Click → inline popover
 * with definition and source citation pulled from /api/glossary.
 */
export default function VocabularyChips({ terms = [] }) {
  const [glossary, setGlossary] = useState({});
  const [openTerm, setOpenTerm] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    let alive = true;
    loadGlossary().then((g) => { if (alive) setGlossary(g); });
    return () => { alive = false; };
  }, []);

  // Close popover on outside click / escape
  useEffect(() => {
    if (!openTerm) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpenTerm(null);
      }
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpenTerm(null); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [openTerm]);

  if (!terms.length) return null;
  const active = openTerm ? glossary[openTerm] : null;

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex flex-wrap gap-2 mt-2" data-testid="vocab-chips">
        {terms.map((v) => {
          const has = !!glossary[v];
          const isOpen = openTerm === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => setOpenTerm(isOpen ? null : v)}
              className={`px-3 py-1 text-xs rounded-full border transition flex items-center gap-1.5 ${
                isOpen
                  ? "border-[#C9A961] bg-[#C9A961]/15 text-[#C9A961]"
                  : "border-[#C9A961]/30 text-[#C9A961] hover:border-[#C9A961]/65 hover:bg-[#C9A961]/10"
              }`}
              aria-expanded={isOpen}
              data-testid={`vocab-chip-${v.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {has && <BookOpen className="w-3 h-3 opacity-70" strokeWidth={1.6} />}
              <span>{v}</span>
            </button>
          );
        })}
      </div>

      {active && (
        <div
          className="mt-4 p-4 bg-[#1A1816] border border-[#C9A961]/40 rounded-md shadow-lg"
          data-testid={`vocab-definition-${openTerm.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="overline text-[10px] mb-1">Definition</div>
              <div
                className="text-xl text-[#C9A961]"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {active.term}
              </div>
            </div>
            <button
              onClick={() => setOpenTerm(null)}
              className="text-[#8A8782] hover:text-[#C9A961]"
              aria-label="Close definition"
              data-testid="vocab-definition-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-[#D1CFC9] leading-relaxed mb-3">
            {active.definition}
          </p>
          {active.source && (
            <p className="text-xs text-[#8A8782] italic border-t border-[#C9A961]/15 pt-2">
              <span className="text-[#C9A961]">Source: </span>
              {active.source.author}
              {active.source.year ? ` (${active.source.year}). ` : ". "}
              <em>{active.source.title}</em>
              {active.source.publisher ? `. ${active.source.publisher}.` : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
